import { apiClient } from './apiClient';
import { LocationOnlyPage, CreateLocationOnlyPageRequest } from '../types/locationOnlyPage';

export const locationOnlyPageService = {
  // List all location-only pages for tenant
  async list(tenantId: string, status?: string) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);

    const response = await apiClient.get<LocationOnlyPage[]>(
      `/location-only-pages/?tenant_id=${tenantId}&${params}`
    );
    return response.data;
  },

  // Get single location-only page
  async get(id: string) {
    const response = await apiClient.get<LocationOnlyPage>(`/location-only-pages/${id}`);
    return response.data;
  },

  // Create new location-only page via crew
  async create(data: CreateLocationOnlyPageRequest) {
    const response = await apiClient.post<LocationOnlyPage>('/location-only-pages/', data);
    return response.data;
  },

  // Trigger crew to generate location-only page
  async generateWithCrew(inputs: {
    location: string;
    keyword?: string;
    tenant_id: string;
    existing_page_id?: string;
    website?: string;
    services_offered?: string;
    credentials?: string;
    tone?: string;
    image_reference?: string;
    // Brand Bible fields required by crew service
    context?: string;
    description?: string;
    ideal_client?: string;
    case_studies?: string;
    // SEO Configuration fields (optional - uses defaults if not provided)
    word_count_min?: number;
    word_count_max?: number;
    keyword_density_min?: number;
    keyword_density_max?: number;
    external_links?: number;
    internal_links_per_300_words?: number;
    meta_description_min?: number;
    meta_description_max?: number;
    keyword_in_first_paragraph?: boolean;
    keyword_in_headings?: boolean;
    use_bullet_points?: boolean;
    // Conversion elements
    show_testimonials?: boolean;
    embed_map?: boolean;
    sticky_cta?: boolean;
    faq_schema?: boolean;
  }) {
    const response = await apiClient.post('/crews/crew-location-only/kickoff', {
      inputs,
    });
    return response.data;
  },

  // Update existing page
  async update(id: string, data: Partial<LocationOnlyPage>) {
    const response = await apiClient.put<LocationOnlyPage>(`/location-only-pages/${id}`, data);
    return response.data;
  },

  // Delete page
  async delete(id: string) {
    const response = await apiClient.delete(`/location-only-pages/${id}`);
    return response.data;
  },

  // Publish page
  async publish(id: string) {
    const response = await apiClient.post(`/location-only-pages/${id}/publish`);
    return response.data;
  },

  // Get keyword suggestions
  async getKeywordSuggestions(keyword: string) {
    const response = await apiClient.post<{ seed_keyword: string; suggestions: any[] }>(
      '/location-pages/keyword-suggestions',
      {
        keyword,
        country: 'us', // Default to US
        limit: 3, // We only need top 3
      }
    );
    return response.data;
  },

  // Get available templates
  async getTemplates() {
    const response = await apiClient.get<{ id: string; name: string; description: string }[]>('/location-only-pages/templates');
    return response.data;
  },
};
